import { ActionsPanel } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/ActionsPanel.tsx';
import { BankAccountFieldsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/BankAccountFieldsCard.tsx';
import { ReviewPanel } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/ReviewPanel.tsx';
import { useBankAccountDetailsController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/useBankAccountDetailsController.ts';
import { AuditDetailView } from '@dealstack/frontend/shared/components/Audit/AuditLayout/AuditDetailView.tsx';
import { BankAccountDrawer } from '@dealstack/frontend/shared/components/BankAccountDrawer/BankAccountDrawer.tsx';
import { DetailsPagination } from '@dealstack/frontend/shared/components/DetailsPagination/DetailsPagination.tsx';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { DetailsPanels } from '@dealstack/frontend/shared/layout/DetailsPanels/DetailsPanels.tsx';
import { PageAbsoluteContainer } from '@dealstack/frontend/shared/layout/PageAbsoluteContainer/PageAbsoluteContainer.tsx';
import { PageHeader } from '@dealstack/frontend/shared/layout/PageHeader/PageHeader.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	DATE_FORMATS,
	formatStringDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date.ts';
import { AuditAction } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { Flex, Portal, Text } from '@mantine/core';

export const BankAccountDetails = () => {
	const tMipManage = useTranslationMipManage();
	const {
		bankAccount,
		isLoading,
		isError,
		timelineEvents,
		isHistoryLoading,
		activeTab,
		setActiveTab,
		selectedEvent,
		selectedEventTitle,
		selectedEventDiff,
		selectEvent,
		pagination,
		reviewBankAccount,
		isReviewPending,
		decline,
		canEdit,
		editableBankAccount,
		personId,
		isEditOpen,
		openEdit,
		closeEdit,
		refreshAfterEdit,
	} = useBankAccountDetailsController();

	return (
		<PageAbsoluteContainer>
			<Portal target="#mip-manage-header-slot">
				{pagination.currentIndex >= 0 && (
					<DetailsPagination
						label={tMipManage('bank_accounts.pagination.label')}
						counter={tMipManage('details_pagination.counter', {
							current: pagination.currentIndex + 1,
							total: pagination.total,
						})}
						prevDisabled={pagination.prevDisabled}
						nextDisabled={pagination.nextDisabled}
						onPrev={pagination.goPrev}
						onNext={pagination.goNext}
					/>
				)}
			</Portal>

			<PageHeader pageTitle={bankAccount?.ownerName ?? ''} hideBreadcrumbs />

			<DetailsPanels
				leadingPanel={
					bankAccount && (
						<ReviewPanel
							personId={bankAccount.personId}
							timelineEvents={timelineEvents}
							isHistoryLoading={isHistoryLoading}
							activeTab={activeTab}
							onTabChange={setActiveTab}
							selectedEventId={selectedEvent?.audit.id}
							onSelectEvent={selectEvent}
						/>
					)
				}
				sidePanel={
					bankAccount &&
					!selectedEvent && (
						<ActionsPanel
							bankAccount={bankAccount}
							isReviewPending={isReviewPending}
							onReview={(action) => void reviewBankAccount(action)}
							decline={decline}
						/>
					)
				}
			>
				{isLoading && <InlineLoader />}

				{isError && (
					<Flex p="1.5rem">
						<Text size="sm" c="dark.0">
							{tMipManage('bank_accounts.details.load_error')}
						</Text>
					</Flex>
				)}

				{bankAccount && selectedEvent && (
					<Flex p="1rem 0" w="100%">
						<AuditDetailView
							title={selectedEventTitle}
							timestamp={formatStringDateToUiWithFallback(
								selectedEvent.audit.createdAt,
								DATE_FORMATS.TIMELINE_EVENT_DATE_TIME
							)}
							action={AuditAction.UPDATE}
							data={selectedEventDiff}
							dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_BANK_ACCOUNT_EVENT_DIFF}
						/>
					</Flex>
				)}

				{bankAccount && !selectedEvent && (
					<BankAccountFieldsCard
						bankAccount={bankAccount}
						onEdit={canEdit && editableBankAccount ? openEdit : undefined}
					/>
				)}
			</DetailsPanels>

			{editableBankAccount && (
				<BankAccountDrawer
					opened={isEditOpen}
					onClose={closeEdit}
					hasPermission={canEdit}
					bankAccount={editableBankAccount}
					personId={personId}
					onSaved={refreshAfterEdit}
				/>
			)}
		</PageAbsoluteContainer>
	);
};
